/**
 * Return/Devolution data models.
 */

import { BaseEntity, TimestampMixin, SearchableEntity } from './base';
import { OrderProduct } from './order';

export interface ReturnProduct extends OrderProduct {
  sourceSaleId: string;
  sourceSaleNumber?: number;
  sourceSaleProductId: string;
}

export interface ApprovedSaleLine {
  saleId: string;
  saleNumber?: number;
  clientId: string;
  clientName: string;
  created?: Date;
  sourceSaleProductId: string;
  productId: string;
  code: string;
  name: string;
  brand?: string;
  description?: string;
  price: number;
  discount?: number;
  quantitySold: number;
  quantityReturned: number;
  quantityReturnable: number;
  imageUrl?: string;
  taxId?: string;
  taxRate?: number;
  warehouseId?: string;
}

export interface ApprovedSalesLinesResult {
  items: ApprovedSaleLine[];
  totalCount: number;
}

export interface ReturnBase extends SearchableEntity {
  originalOrderId?: string; // Link to original sale/order
  clientId: string;
  clientName: string;
  creatorSalesPerson: string; // Creator sales person email
  paymentMethod?: string; // Payment method name (optional)
  paymentCurrency?: string; // Currency used for payment (currency or referenceCurrency)
  subtotal: number;
  ivaAmount?: number; // IVA amount for transparency
  total: number;
  costSubtotal?: number;
  grossProfit?: number;
  exchangeRate?: number; // USD exchange rate used at time of return
  deleted?: boolean; // Soft delete flag
  comment?: string;
  products: ReturnProduct[];
}

export interface Return extends ReturnBase, BaseEntity, TimestampMixin {
  approvalState: 'approved' | 'pending' | 'rejected';
  returnNumber?: number;
}

/**
 * Deduplicated source sales for list/detail: prefers {@link ReturnProduct#sourceSaleNumber},
 * otherwise a short source sale id. Falls back to {@link ReturnBase#originalOrderId} when
 * there are no line-level sale references.
 */
export function getReturnSourceSalesDisplay(
  ret: Pick<ReturnBase, 'products' | 'originalOrderId'>
): string {
  const products = ret.products || [];
  type PerSale = { n?: number; id: string };
  const bySaleId = new Map<string, PerSale>();

  for (const p of products) {
    const id = String(p.sourceSaleId || '').trim();
    if (!id) continue;
    const rawNum = p.sourceSaleNumber;
    const num =
      rawNum !== undefined && rawNum !== null && Number.isFinite(Number(rawNum))
        ? Number(rawNum)
        : undefined;
    const existing = bySaleId.get(id);
    if (!existing) {
      bySaleId.set(id, { id, n: num });
    } else if (existing.n === undefined && num !== undefined) {
      existing.n = num;
    }
  }

  if (bySaleId.size > 0) {
    const list = Array.from(bySaleId.values());
    list.sort((a, b) => {
      if (a.n != null && b.n != null) return a.n - b.n;
      if (a.n != null) return -1;
      if (b.n != null) return 1;
      return a.id.localeCompare(b.id);
    });
    return list
      .map(s => {
        if (s.n != null) {
          return `#${s.n}`;
        }
        return s.id.length > 12 ? `${s.id.slice(0, 8)}…` : s.id;
      })
      .join(', ');
  }

  return (ret.originalOrderId || '').trim();
}

export interface ReturnCreate extends Omit<ReturnBase, 'keywords'> {
  // For creating new returns - keywords are generated automatically
}

export interface ReturnUpdate extends Partial<ReturnBase> {
  approvalState?: 'approved' | 'pending' | 'rejected';
  updated?: Date;
}

// Default values for returns
export const RETURN_DEFAULTS = {
  approvalState: 'pending' as const,
  subtotal: 0,
  total: 0,
  products: [],
} as const;

// Return search and filter types
export interface ReturnFilters {
  searchTerm?: string;
  originalOrderId?: string;
  clientId?: string;
  clientName?: string;
  creatorSalesPerson?: string;
  approvalState?: 'approved' | 'pending' | 'rejected';
  dateFrom?: Date;
  dateTo?: Date;
  minTotal?: number;
  maxTotal?: number;
}

export interface ReturnQueryResult {
  returns: Return[];
  lastDoc?: any; // Firestore document snapshot
  hasMore: boolean;
  totalCount?: number;
}

// Return statistics
export interface ReturnStats {
  totalReturns: number;
  totalAmount: number;
  averageReturnValue: number;
  pendingReturns: number;
  approvedReturns: number;
  rejectedReturns: number;
}
